import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'

import {
  GCP_PROJECT_ID_DUPLICATION,
  GCP_PROJECT_ID_VALIDATION
} from 'components/common/mapped-entities/gcp/authentication/gcp-validation-utils'
import { type TeamExternalIntegrationGcp } from 'types/api/status-checks/status-check'

export enum GcpAuthRadioChoice {
  Existing = 'existing',
  New = 'new'
}

export interface GcpAuthenticationFormValuesNew {
  projectId: string
  serviceAccountId: string
}

export interface GcpAuthenticationFormValuesExisting
  extends GcpAuthenticationFormValuesNew {
  authRadioChoice: GcpAuthRadioChoice | undefined
}

/**
 * Builds the Yup schema for GCP authentication form validation.
 *
 * `isNewAuthOnly` is required because there are two different form shapes:
 * - `true`: form only allows creating a new authentication.
 * - `false`: form supports selecting existing or creating new authentication.
 *   GcpAuthRadioChoice is expected to be present in the consuming form.
 */
function getGcpAuthenticationFormSchema({
  integrations,
  isNewAuthOnly
}: {
  integrations: TeamExternalIntegrationGcp[]
  isNewAuthOnly: boolean
}) {
  const integrationsSet = new Set(
    integrations.map(({ domain }) => domain.toUpperCase())
  )

  const baseProjectIdSchema = yup
    .string()
    .required()
    .matches(GCP_PROJECT_ID_VALIDATION.regex, {
      message: GCP_PROJECT_ID_VALIDATION.message
    })

  // TODO: replace with validation when known
  const baseServiceAccountIdSchema = yup.string().required()
  // .matches(GCP_PROJECT_ID_VALIDATION.regex, {
  //   message: GCP_PROJECT_ID_VALIDATION.message
  // })

  const newProjectIdSchema = baseProjectIdSchema.test(
    'not-duplicate',
    GCP_PROJECT_ID_DUPLICATION.message,
    (value) => GCP_PROJECT_ID_DUPLICATION.test(value, integrationsSet)
  )

  const existingProjectIdSchema = baseProjectIdSchema.test(
    'exists',
    'Authentication not set',
    (value) => value !== undefined && integrationsSet.has(value.toUpperCase())
  )

  if (isNewAuthOnly) {
    return yup.object({
      projectId: newProjectIdSchema,
      serviceAccountId: baseServiceAccountIdSchema
    })
  }

  return yup.object({
    authRadioChoice: yup
      .string()
      .oneOf(Object.values(GcpAuthRadioChoice))
      .nullable(),
    projectId: baseProjectIdSchema.when('authRadioChoice', {
      is: GcpAuthRadioChoice.Existing,
      then: existingProjectIdSchema,
      otherwise: newProjectIdSchema
    }),
    serviceAccountId: baseServiceAccountIdSchema
  })
}

/**
 * Provides form initializers for when only creating new authentication is allowed.
 */
export function getGcpAuthenticationFormInitializersNew(
  integrations: TeamExternalIntegrationGcp[]
) {
  return {
    defaultValues: {
      projectId: '',
      serviceAccountId: ''
    } satisfies GcpAuthenticationFormValuesNew,
    resolver: yupResolver(
      getGcpAuthenticationFormSchema({
        integrations,
        isNewAuthOnly: true
      })
    )
  }
}

/**
 * Provides form initializers for when the option to add an existing authentication is available.
 * `existingProjectId` should be provided only if we're editing an existing authentication and
 * want to pre-populate the form with that project ID.
 */
export function getGcpAuthenticationFormInitializersExisting({
  existingProjectId,
  integrations
}: {
  existingProjectId?: string
  integrations: TeamExternalIntegrationGcp[]
}) {
  const defaultValues: GcpAuthenticationFormValuesExisting = integrations.length
    ? {
        authRadioChoice: GcpAuthRadioChoice.Existing,
        projectId: existingProjectId ?? integrations[0]?.domain ?? '',
        serviceAccountId: ''
      }
    : {
        authRadioChoice: undefined,
        projectId: '',
        serviceAccountId: ''
      }

  return {
    defaultValues,
    resolver: yupResolver(
      getGcpAuthenticationFormSchema({
        integrations,
        isNewAuthOnly: false
      })
    )
  }
}
